import React, { useEffect } from "react"
import { graphql } from "gatsby"
import MainLayout from "@/layouts/MainLayout"
import SanityContentWrapper from "@/components/SanityContentWrapper"
import BlogPostHeader from "@/components/BlogPostHeader"
import SocialShare from "@/components/SocialShare"
import Longform from "@/components/Longform"
import NewsletterSignup from "@/components/NewsletterSignup"
import { Link, useTranslation } from "@zestia/gatsby-plugin-react-i18next"

import SharedHead from "@/components/SharedHead"
import ArticleNavigation from "@/components/ArticleNavigation"
import ScrollSpy from "@/components/ScrollSpy"
import BlogPostPreviewRow from "@/components/BlogPostPreviewRow"
import transformBlogPosts from "@/utils/transformBlogPosts"
import urlFor from "@/utils/sanity-url-builder"
import { slugify } from "../../../shared-utils/slugify"
import createAvailableSlugsFromSanityTranslationMetadata from "@/utils/create-available-slugs-from-sanity-translation-metadata"

export const Head = props => {
  const {
    data: {
      sanityBlogPost: { seo, hidden, coverImage, title, summary },
    },
  } = props
  return (
    <SharedHead
      {...props}
      title={seo?.seoTitle || title}
      description={seo?.seoDescription || summary}
      appendTitle={false}
      noIndex={hidden}
      image={coverImage && urlFor(coverImage).width(600).quality(90).url()}
    />
  )
}

const SanityBlogPostLayout = ({ data, pageContext }) => {
  const { t } = useTranslation()

  // Get translation metadata and create availableSlugs array (optional)
  const availableSlugs = createAvailableSlugsFromSanityTranslationMetadata(
    data.allSanityTranslationMetadata,
    pageContext,
    "/blog",
  )

  // Destructure the blog post from the query
  const { intent, _rawContent, categories } = data.sanityBlogPost

  // Get all the h2s from the blog post, flatten their children into a string and create an id
  let subHeadings = _rawContent
    .filter(block => block._type === "block" && block.style === "h2")
    .map(h2 => {
      const flattenedHeading = h2.children.map(child => child.text).join("")
      const id = `#${slugify(flattenedHeading)}`

      return {
        id,
        text: flattenedHeading,
      }
    })

  // Combine related blogposts from MDX and Sanity, then sort by date, and limit to 4
  let relatedBlogposts = transformBlogPosts(data)
    .sort((a, b) => {
      const dateA = a.frontmatter?.date || a.publishedAt
      const dateB = b.frontmatter?.date || b.publishedAt
      return new Date(dateB) - new Date(dateA)
    })
    .slice(0, 4)

  return (
    <MainLayout
      className="blog-post"
      availableSlugs={availableSlugs ?? []}
      trackingData={{
        intent: intent,
        content_category: `blog-${categories[0]}`,
      }}
    >
      <div className="container-fluid">
        <section className="blog-post__top">
          {/* Back button */}
          <Link to="/blog" className="text-link text-link--arrow-before">
            {t("blog.back-to-all-posts")}
          </Link>

          <BlogPostHeader post={data.sanityBlogPost} />
        </section>

        <section className="blog-post__content-wrapper">
          {subHeadings.length > 0 && (
            <div className="blog-post__navigation-wrapper">
              {/* Shows on desktop */}
              <ArticleNavigation links={subHeadings} />

              {/* Shows on mobile */}
              <ArticleNavigation links={subHeadings} mobile="true" />
            </div>
          )}

          <Longform
            className={`blog-post__content ${
              !subHeadings.length ? "blog-post__content--centered" : ""
            }`}
          >
            <ScrollSpy options={{ limitToQuerySelector: "h2", offset: 80 }}>
              <SanityContentWrapper>{_rawContent}</SanityContentWrapper>
            </ScrollSpy>

            <NewsletterSignup className="mt-10 newsletter-signup--no-border-bottom newsletter-signup--no-padding-bottom" />
          </Longform>

          <div className="blog-post__social-share-wrapper">
            <SocialShare />
          </div>
        </section>
      </div>

      <div className="container-fluid blog-post__related-blogposts pt-16 pb-16">
        <BlogPostPreviewRow
          posts={relatedBlogposts}
          title={t("blog.related-posts")}
        />
      </div>
    </MainLayout>
  )
}

export const query = graphql`
  query ($language: String, $id: String!, $categories: [String]) {
    ...Locales
    ...Integrations

    allSanityTranslationMetadata {
      nodes {
        translations {
          value {
            ... on SanityBlogPost {
              _id
              language
              slug {
                current
              }
            }
          }
        }
      }
    }

    sanityBlogPost(_id: { eq: $id }) {
      _id
      _rawContent

      seo {
        seoTitle
        seoDescription
      }
      intent
      title
      hidden
      hiddenFromPageOnly
      summary
      categories
      slug {
        current
      }
      author {
        name
      }
      wistiaVideo {
        id
      }
      coverImage {
        ...ImageWithPreview
      }
      publishedAt
      internal {
        type
      }
    }

    allMdx(
      filter: {
        fields: {
          lang: { eq: $language }
          path: { regex: "/(mdxPages/blog)/" }
        }
        frontmatter: { hidden: { ne: true }, categories: { in: $categories } }
        id: { ne: $id }
      }
    ) {
      nodes {
        internal {
          type
        }
        frontmatter {
          title
          date
          categories
          slug
          preview_image {
            alt
            src {
              childImageSharp {
                gatsbyImageData(formats: AUTO, width: 400, placeholder: BLURRED)
              }
            }
          }
        }
      }
    }

    allSanityBlogPost(
      filter: {
        categories: { in: $categories }
        _id: { ne: $id }
        hidden: { ne: true }
        hiddenFromPageOnly: { ne: true }
        language: { eq: $language }
      }
    ) {
      nodes {
        internal {
          type
        }
        title
        publishedAt
        categories
        slug {
          current
        }
        coverImage {
          ...ImageWithPreview
        }
      }
    }

    allFile(
      filter: {
        sourceInstanceName: { eq: "images" }
        relativePath: { eq: "blog/generic-image.png" }
      }
    ) {
      edges {
        node {
          childImageSharp {
            gatsbyImageData
          }
        }
      }
    }
  }
`

export default SanityBlogPostLayout
